import { useId, useState } from 'react';
import { hintKeyForStep } from '@/flow/copy/support';
import { useT } from '@/i18n/runtime';
import type { TenantConfig } from '@/types';

interface SupportAffordanceProps {
  readonly tenant: TenantConfig;
  /** The flow step being shown — selects the per-step hint. */
  readonly step: string;
}

/**
 * "Having trouble?" — the droom's route to a human (help catch-up Tier 1 #14).
 *
 * Before this, ~17 customer screens carried NO help button, NO FAQ and NO
 * support contact anywhere. The one person in this product who is doing this
 * once, on a phone, under stress, had nowhere to go; the trained officer at a
 * desk had a 7-locale help system. This closes most of that gap without
 * importing the help package at all.
 *
 * Deliberate shape:
 *  • INLINE DISCLOSURE, not a modal. One-shot mobile needs per-step disclosure;
 *    a focus-trapping sheet over a capture screen is the officer-console mistake
 *    repeated on the customer (help plan DO-NOT-COPY #4).
 *  • NEVER an assistant. The customer's escalation path is a human — the officer
 *    on the call, the tenant's support before it (DO-NOT-COPY #3).
 *  • Honest when empty. A tenant with no configured channel gets a true
 *    sentence, not an invented number.
 *  • On the call, it says so and offers nothing else: the human is already there.
 *  • Links carry `rel="noopener noreferrer"` and open in a new tab, so help can
 *    never navigate the customer OUT of their session.
 */
export function SupportAffordance({ tenant, step }: SupportAffordanceProps): JSX.Element {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const support = tenant.support;
  const onCall = step === 'call';

  return (
    <div className="support-affordance">
      <button
        type="button"
        className="support-trigger"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
      >
        {t('droom.support.trigger')}
      </button>

      {open ? (
        <div id={panelId} className="support-panel" role="group">
          <h2 className="support-title">{t('droom.support.title')}</h2>

          {/* What to try on THIS screen, before contacting anyone. */}
          <p className="support-hint">{t(hintKeyForStep(step))}</p>

          {onCall ? (
            <p className="support-line">{t('droom.support.oncall')}</p>
          ) : (
            <>
              <p className="support-line">
                {t('droom.support.intro.named', { tenantName: tenant.name })}
              </p>

              {support?.phone || support?.email || support?.url ? (
                <ul className="support-channels">
                  {support.phone ? (
                    <li>
                      <a className="support-channel" href={`tel:${support.phone.replace(/\s/g, '')}`}>
                        {t('droom.support.phone.label')} · {support.phone}
                      </a>
                    </li>
                  ) : null}
                  {support.email ? (
                    <li>
                      <a className="support-channel" href={`mailto:${support.email}`}>
                        {t('droom.support.email.label')} · {support.email}
                      </a>
                    </li>
                  ) : null}
                  {support.url ? (
                    <li>
                      <a
                        className="support-channel"
                        href={support.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('droom.support.web.label')}
                      </a>
                    </li>
                  ) : null}
                </ul>
              ) : (
                // No invented contact. What we say here is true of every session.
                <p className="support-line support-line--muted">
                  {t('droom.support.none')}
                </p>
              )}

              {support?.hours ? (
                <p className="support-line support-line--muted">
                  {t('droom.support.hours', { hours: support.hours })}
                </p>
              ) : null}
            </>
          )}

          {/* The boundary, stated on every step: support helps you USE this page;
              only the verifier decides the outcome. */}
          <p className="support-boundary">{t('droom.support.boundary')}</p>

          <button type="button" className="support-close" onClick={() => setOpen(false)}>
            {t('droom.support.close')}
          </button>
        </div>
      ) : null}
    </div>
  );
}
